'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ClassControlsProps {
  onToggleWhiteboard: () => void;
  onToggleBreakout: () => void;
}

const ClassControls = ({ onToggleWhiteboard, onToggleBreakout }: ClassControlsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center space-x-4">
            <div className="w-12 h-12 bg-muted rounded-full animate-pulse"></div>
            <div className="w-12 h-12 bg-muted rounded-full animate-pulse"></div>
            <div className="w-12 h-12 bg-muted rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-lg z-40">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsMuted(!isMuted)}
              className={`p-3 rounded-full transition-all ${
                isMuted ? 'bg-error text-white' : 'bg-muted text-foreground hover:bg-background'
              }`}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              <Icon 
                name={isMuted ? 'MicrophoneIcon' : 'MicrophoneIcon'} 
                size={24} 
                variant="solid" 
              />
            </button>
            <button
              onClick={() => setIsVideoOn(!isVideoOn)}
              className={`p-3 rounded-full transition-all ${
                !isVideoOn ? 'bg-error text-white' : 'bg-muted text-foreground hover:bg-background'
              }`}
              aria-label={isVideoOn ? 'Turn off video' : 'Turn on video'}
            >
              <Icon 
                name={isVideoOn ? 'VideoCameraIcon' : 'VideoCameraSlashIcon'} 
                size={24} 
                variant="solid" 
              />
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsScreenSharing(!isScreenSharing)}
              className={`p-3 rounded-full transition-all ${
                isScreenSharing ? 'bg-primary text-white' : 'bg-muted text-foreground hover:bg-background'
              }`}
              aria-label={isScreenSharing ? 'Stop sharing' : 'Share screen'}
            >
              <Icon name="ComputerDesktopIcon" size={24} variant={isScreenSharing ? 'solid' : 'outline'} />
            </button>
            <button
              onClick={onToggleWhiteboard}
              className="p-3 rounded-full bg-muted text-foreground hover:bg-background transition-all"
              aria-label="Toggle whiteboard"
            >
              <Icon name="PencilSquareIcon" size={24} variant="outline" />
            </button>
            <button
              onClick={() => setIsRecording(!isRecording)}
              className={`p-3 rounded-full transition-all ${
                isRecording ? 'bg-error text-white animate-pulse' : 'bg-muted text-foreground hover:bg-background'
              }`}
              aria-label={isRecording ? 'Stop recording' : 'Start recording'}
            >
              <Icon name="VideoCameraIcon" size={24} variant={isRecording ? 'solid' : 'outline'} />
            </button>
            <button
              onClick={onToggleBreakout}
              className="p-3 rounded-full bg-muted text-foreground hover:bg-background transition-all"
              aria-label="Breakout rooms"
            >
              <Icon name="UserGroupIcon" size={24} variant="outline" />
            </button>
            <button
              onClick={() => setIsHandRaised(!isHandRaised)}
              className={`p-3 rounded-full transition-all ${
                isHandRaised ? 'bg-warning text-white' : 'bg-muted text-foreground hover:bg-background'
              }`}
              aria-label={isHandRaised ? 'Lower hand' : 'Raise hand'}
            >
              <Icon name="HandRaisedIcon" size={24} variant={isHandRaised ? 'solid' : 'outline'} />
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button className="p-3 rounded-full bg-muted text-foreground hover:bg-background transition-all">
              <Icon name="Cog6ToothIcon" size={24} variant="outline" />
            </button>
            <button className="px-6 py-3 bg-error text-white rounded-full hover:bg-opacity-90 transition-all font-semibold">
              Leave Class
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassControls;